import { Signal } from "../core/Signal.js";
import { Matrix3d } from "../geom/Matrix3d.js";
import { Point3d } from "../geom/Point3d.js";
import { Transform } from "../geom/Transform.js";
import { ITransformMode } from "../geom/transformmode/ITransformMode.js";
import { TransformMode } from "../geom/transformmode/TransformMode.js";
import { Calc } from "../math/Calc.js";
import { object } from "../util/ObjectUtil.js";
import { ActorNonOptions, Actor, Pointer, ActorOptions } from "./Actor.js";
import { Clamp, ClampOptions } from "./Clamp.js";
import { Snap, SnapOptions } from "./Snap.js";

export enum Trigger {
    NONE = 0,
    START = 1 << 0,
    MOVE = 1 << 1,
    CLEAR = 1 << 2,
    ALL = START | MOVE | CLEAR
}

export interface DragOptions extends ActorOptions {
    /**
     * Should the element be dragged horizontally.
     */
    dragX?: boolean;
    /**
     * Should the element be dragged vertically.
     */
    dragY?: boolean;
    /**
     * The transform mode used for the coordinate transformation.
     */
    transformMode?: ITransformMode;
    /**
     * If set to true, then css transform is used.
     * If set to false, then css top/left is used.
     * 
     * @remarks
     * Its recommeded to prefer transform over top/left.
     */
    useTransform?: boolean;
    /**
     * The clamping options. If null no clamping is applied.
     */
    clamp?: ClampOptions;
    /**
     * When should the clamping applied:
     * * "down" The first pointer is added.
     * * "move" The pointers are moving (live).
     * * "clear" All pointers are removed.
     */
    clampTrigger?: Trigger;
    /**
     * The snapping options. If null no snapping is applied.
     */
    snap?: SnapOptions;
    /**
     * When should the snapping applied:
     * * "down" The first pointer is added.
     * * "move" The pointers are moving (live).
     * * "clear" All pointers are removed.
     */
    snapTrigger?: Trigger;
}

export class Drag extends Actor<Pointer, DragOptions> implements DragOptions {
    public static DEFAULT: DragOptions = {
        dragX: true,
        dragY: true,
        transformMode: TransformMode.Rectangle,
        useTransform: true,
        clamp: null,
        clampTrigger: Trigger.CLEAR,
        snap: null,
        snapTrigger: Trigger.CLEAR
    };

    public onDrag = Signal.create();

    public dragX: boolean;
    public dragY: boolean;
    public useTransform: boolean;
    public clamp: ClampOptions;
    public clampTrigger: Trigger;
    public snap: SnapOptions;
    public snapTrigger: Trigger;

    private _target: HTMLElement = null;
    private _onTrigger = Signal.create<[Trigger]>();
    private _transform: Transform = new Transform();
    private _matrix: Matrix3d;

    constructor(element: HTMLElement = null, options: DragOptions = null) {
        super(null, ActorNonOptions);
        // this.element = element;

        if (options !== ActorNonOptions)
            this.setOptions(options);

        this.element = element;

        var matrix = new Matrix3d();
        this._matrix = matrix;
        var pivot = new Point3d();
        var center = new Point3d();
        var local = new Point3d();

        var down = (pointers: Pointer[]) => {
            for (var pointer of pointers) {
                if (pointer.event.cancelable) {
                    try {
                        pointer.event.preventDefault();
                    } catch (error) {
                    }
                }
            }

            if (this.count === 1)
                this._onTrigger.dispatch(Trigger.START);

            center.copyFrom2d(this.center);
            this._transform.update(this.target);
            this._transform.toLocalPoint(center, pivot);
        };

        var up = (pointers: Pointer[]) => {
            if (this.count === 0) {
                this._onTrigger.dispatch(Trigger.CLEAR);
                return;
            }

            center.copyFrom2d(this.center);
            this._transform.update(this.target);
            this._transform.toLocalPoint(center, pivot);
        };

        var move = () => {
            if (!this.dragX && !this.dragY)
                return;

            center.copyFrom2d(this.center);

            this._transform.update(this.target);
            this._transform.toLocalPoint(center, local);
            local.sub(pivot);

            matrix.identity();
            // matrix.appendPositionRaw(local.x, local.y, 0);
            matrix.prependPosition(local);
            matrix.prepend(this._transform.matrix);

            if (this.useTransform)
                dragTransform();
            else
                dragTopLeft();

            this._onTrigger.dispatch(Trigger.MOVE);
            this.onDrag.dispatch();
        };

        var dragTopLeft = () => {
            var left = Calc.parse(this._transform.style.left, 0) + matrix.m41;
            var top = Calc.parse(this._transform.style.top, 0) + matrix.m42;

            if (this.dragX)
                this.target.style.left = left + "px";
            if (this.dragY)
                this.target.style.top = top + "px";
        };

        var dragTransform = () => {
            if (!this.dragX)
                matrix.m41 = this._transform.matrix.m41;

            if (!this.dragY)
                matrix.m42 = this._transform.matrix.m42;

            this.target.style.transform = matrix.getCss3d();
        };

        this.onDown.add(down);
        this.onUp.add(up);
        this.onMove.add(move);
        this._onTrigger.add((trigger) => {
            if (this.snap && (trigger & this.snapTrigger)) {
                this.snap.useTransform = this.useTransform;
                Snap.apply(this.target, this.snap);
            }

            if (this.clamp && (trigger & this.clampTrigger)) {
                this.clamp.transformMode = this.transformMode;
                this.clamp.useTransform = this.useTransform;
                Clamp.apply(this.target, this.clamp);
            }
        });
    }

    public setOptions(options: DragOptions) {
        options = object.merge({}, Drag.DEFAULT, options);
        this.dragX = options.dragX;
        this.dragY = options.dragY;
        this.snap = options.snap;
        this.snapTrigger = options.snapTrigger;
        this.clamp = options.clamp;
        this.clampTrigger = options.clampTrigger;
        this.useTransform = options.useTransform;
        this.transformMode = options.transformMode;
        super.setOptions(options);
    }

    public get target(): HTMLElement {
        return this._target || this.element;
    }

    public set target(target: HTMLElement) {
        if (this._target === target)
            return;
        this._target = target;
        if (target && !this.element)
            this.element = target;
    }

    public get top(): number {
        if (!this.target)
            return 0;
        return this.position.y;
    }

    public get left(): number {
        if (!this.target)
            return 0;
        return this.position.x;
    }

    public get position(): Point3d {
        if (!this.target)
            return new Point3d();

        if (!this.useTransform) {
            var style = window.getComputedStyle(this.target);
            return new Point3d(
                Calc.parse(style.left, 0),
                Calc.parse(style.top, 0),
                0
            );
        }

        return Matrix3d.getPosition(this.target);
        // return this._transform.matrix.getPosition();
        // return this._matrix.getPosition();
    }

    public get bottom(): number {
        if (!this.target)
            return this.top;
        return this.top + this.target.offsetHeight;
    }

    public get right(): number {
        if (!this.target)
            return this.left;
        return this.left + this.target.offsetWidth;
    }

    public get transformMode(): ITransformMode {
        return this._transform.mode;
    }

    public set transformMode(mode: ITransformMode) {
        this._transform.mode = mode;
    }

    public dispose(): void {
        this._transform.dispose();
        this._transform = null;
        this._onTrigger.dispose();
        this._onTrigger = null;
        this.onDrag.dispose();
        this.onDrag = null;
        super.dispose();
    }
}